import { failValidation, isType, passValidation } from "./core.js";
export const isLiteral = (type) => isType((candidate, context) => {
    if (candidate === type) {
        return passValidation(context);
    }
    return failValidation(`Expected literal value "${String(type)}", but received "${typeof candidate}${candidate && typeof candidate === "object" ? `: ${candidate.constructor.name}` : ""}`, context);
}, String(type));
// Implementation
export function is(guardOrTypeString) {
    if (typeof guardOrTypeString === "function") {
        const guard = guardOrTypeString;
        return isType((candidate, context) => {
            if (guard(candidate)) {
                return passValidation(context);
            }
            return failValidation(`User-defined type guard failed for value of type "${typeof candidate}"${candidate && typeof candidate === "object" ? `: ${candidate.constructor.name}` : ""}`, context);
        }, "user-defined type guard");
    }
    const typeString = guardOrTypeString;
    return isType((candidate, context) => {
        if (typeString === "null"
            ? candidate === null
            : typeof candidate === typeString) {
            return passValidation(context);
        }
        return failValidation(`Expected "${typeString}", but received "${typeof candidate}"${candidate && typeof candidate === "object" ? `: ${candidate.constructor.name}` : ""}`, context);
    }, typeString);
}
export const isTuple = (...predicates) => isType((candidate, context) => {
    if (!Array.isArray(candidate)) {
        return failValidation(`Expected an array, but received ${typeof candidate}.`, context);
    }
    if (candidate.length !== predicates.length) {
        return failValidation(`Expected an array with length ${predicates.length}, but received array with ${candidate.length} element(s).`, context);
    }
    return predicates.reduce((currentResult, predicate, index) => {
        return predicate.validate(candidate[index], {
            path: `${context.path}[${index}]`,
            currentResult,
        });
    }, context.currentResult);
}, `[${predicates.map((predicate) => predicate.typeDescription).join(", ")}]`);
export const isUnionOf = (...predicates) => isType((candidate, context) => {
    const failures = [];
    for (let predicateIndex = 0; predicateIndex < predicates.length; predicateIndex++) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const result = predicates[predicateIndex].validate(candidate, {
            currentResult: { valid: true },
            path: `${context.path} |(${predicateIndex})`,
        });
        if (result.valid) {
            return result;
        }
        failures.push(result);
    }
    return failValidation(`Expected union of types. The following errors were received:\n${failures
        .map((failure) => failure.errorMessages.map((message) => `\t${message}`).join("\n"))
        .join("\n")}\n`, context);
}, predicates.map((predicate) => predicate.typeDescription).join(" | "));
export const isIntersectionOf = (...predicates) => isType((candidate, context) => {
    const result = predicates.reduce((currentResult, predicate, predicateIndex) => {
        return predicate.validate(candidate, {
            path: `${context.path} &(${predicateIndex})`,
            currentResult,
        });
    }, { valid: true });
    if (result.valid) {
        return passValidation(context);
    }
    return failValidation(`Expected intersection of types. The following errors were received:\n${result.errorMessages.map((message) => `\t${message}`).join("\n")}\n`, context);
}, predicates.map((predicate) => predicate.typeDescription).join(" & "));
const hasOwnProperty = (obj, prop) => {
    return Object.prototype.hasOwnProperty.call(obj, prop);
};
export const isArrayOf = (isElement) => isType((candidate, context) => {
    if (!Array.isArray(candidate)) {
        return failValidation(`Expected an array, but received ${typeof candidate}.`, context);
    }
    return candidate.reduce((currentResult, element, elementIndex) => {
        return isElement.validate(element, {
            path: `${context.path}[${elementIndex}]`,
            currentResult,
        });
    }, context.currentResult);
}, `${isElement.typeDescription}[]`);
export const isRecordOf = (isValue) => isType((candidate, context) => {
    if (typeof candidate !== "object") {
        return failValidation(`Expected an object, but received type "${typeof candidate}".`, context);
    }
    if (candidate === null) {
        return failValidation(`Candidate cannot be null, but was.`, context);
    }
    return Object.entries(candidate).reduce((currentResult, [key, value]) => {
        return isValue.validate(value, {
            path: `${context.path}.${key}`,
            currentResult,
        });
    }, context.currentResult);
}, `{ [key: string]: ${isValue.typeDescription}; }`);
export const isObject = (properties) => isType((candidate, context) => {
    if (typeof candidate !== "object") {
        return failValidation(`Expected an object, but received type "${typeof candidate}".`, context);
    }
    if (candidate === null) {
        return failValidation(`Candidate cannot be null, but was.`, context);
    }
    return Object.entries(properties).reduce((currentResult, [key, isCorrectType]) => {
        return isCorrectType.validate(hasOwnProperty(candidate, key) ? candidate[key] : undefined, { path: `${context.path}.${key}`, currentResult });
    }, context.currentResult);
}, `{ ${Object.entries(properties)
    .map(([key, predicate]) => `${key}: ${predicate.typeDescription};`)
    .join(" ")} }`);
export const isInstanceOf = (constructor) => {
    return isType((candidate, context) => {
        if (typeof candidate !== "object") {
            return failValidation(`Expected an object, but received type "${typeof candidate}".`, context);
        }
        if (candidate === null) {
            return failValidation(`Candidate cannot be null, but was.`, context);
        }
        if (candidate instanceof constructor) {
            return passValidation(context);
        }
        return failValidation(`Expected instance of ${constructor.name} but received: ${candidate.constructor.name}`, context);
    }, `instanceof(${constructor.name})`);
};
export const isKeyOf = (obj) => isType((candidate, context) => {
    if (typeof candidate === "string" && candidate in obj) {
        return passValidation(context);
    }
    return failValidation(`Expected key of object, but received "${typeof candidate}": ${String(candidate)}`, context);
}, Object.keys(obj).join(" | "));
//# sourceMappingURL=type-guards.js.map